import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Swal from "sweetalert2";
import image from "../../images/logo.png";
import { usePasswordReset } from "../../hooks/usePasswordReset"; // Adjust path
import { FaEye, FaEyeSlash } from "react-icons/fa"; // Eye icons from react-icons

function ResetPassword() {
  const { token } = useParams(); // Get token from URL
  const { loading, error, message, resetPassword } = usePasswordReset();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [imageSize, setImageSize] = useState({ width: 1, height: 1 });

  useEffect(() => {
    const img = new Image();
    img.src = image;
    img.onload = () => {
      setImageSize({ width: img.width, height: img.height });
    };
  }, []);

  // Calculate dynamic width
  const aspectRatio = imageSize.width / imageSize.height;
  const cardWidth = aspectRatio > 1.5 ? "max-w-5xl" : "max-w-3xl";

  useEffect(() => {
    if (error) {
      Swal.fire({
        icon: "error",
        title: "Reset Failed",
        text: error,
        confirmButtonColor: "#dc2626",
        timer: 3000,
        timerProgressBar: true,
      });
    }
    if (message) {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: message,
        confirmButtonColor: "#16a34a",
        timer: 3000,
        timerProgressBar: true,
      }).then(() => {
        window.location.href = "/"; // Redirect after success
      });
    }
  }, [error, message]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const password = e.target.password.value;
    const confirmPassword = e.target.confirmPassword.value;

    // Basic client-side validation
    if (password !== confirmPassword) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Passwords do not match!",
        confirmButtonColor: "#dc2626",
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    resetPassword(token, password);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="max-w-6xl w-full bg-white rounded-xl shadow-xl overflow-hidden lg:flex">
        <div className="w-full p-6 lg:p-8 lg:w-1/2">
          <form className="max-w-md mx-auto" onSubmit={handleSubmit}>
            <h3 className="text-slate-900 text-2xl lg:text-3xl font-bold mb-8 text-center">
              Reset Password
            </h3>

            <div className="space-y-6">
              <div className="relative">
                <label className="text-sm text-slate-800 font-medium mb-2 block">
                  New Password
                </label>
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  className="bg-slate-100 w-full text-sm text-slate-800 px-4 py-3 rounded-md outline-none border focus:border-green-600 focus:bg-transparent pr-12"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-10 text-gray-500 hover:text-green-600 focus:outline-none"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              <div className="relative">
                <label className="text-sm text-slate-800 font-medium mb-2 block">
                  Confirm Password
                </label>
                <input
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  className="bg-slate-100 w-full text-sm text-slate-800 px-4 py-3 rounded-md outline-none border focus:border-green-600 focus:bg-transparent pr-12"
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-10 text-gray-500 hover:text-green-600 focus:outline-none"
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div className="!mt-12">
              <button
                type="submit"
                disabled={loading}
                className={`w-full shadow-xl py-2.5 px-4 text-sm font-semibold rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none ${
                  loading ? "opacity-75 cursor-not-allowed" : ""
                }`}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </div>

            <div className="my-4 flex items-center gap-4">
              <hr className="w-full border-slate-300" />
              <p className="text-sm text-slate-800 text-center">or</p>
              <hr className="w-full border-slate-300" />
            </div>

            <div className="space-x-6 flex justify-center">
              <div className="text-sm">
                <Link
                  to="/signin"
                  className="text-green-600 hover:text-green-500 font-medium"
                >
                  Back to Sign In
                </Link>
              </div>
            </div>
          </form>
        </div>

        <div className="relative hidden lg:flex lg:w-1/2">
          <img
            alt="Sign in poster"
            src={image}
            className="w-full h-auto object-cover"
            style={{ aspectRatio: `${imageSize.width} / ${imageSize.height}` }}
          />
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
